import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Alert,
    InputAdornment,
    IconButton,
    CircularProgress,
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Person,
    Lock,
} from '@mui/icons-material';
import PageContainer from '../components/layout/PageContainer';
import { login, isAuthenticated } from '../services/authService';

const Login: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [errorType, setErrorType] = useState<'error' | 'warning' | 'info'>('error');
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Get the intended destination from location state, default to home
    const from = (location.state as any)?.from?.pathname || '/';
    
    // Get session expiry message if present
    const sessionMessage = (location.state as any)?.message;

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated()) {
            navigate(from, { replace: true });
        }
    }, [navigate, from]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setErrorType('error');

        try {
            await login(username, password);
            navigate(from, { replace: true });
        } catch (err: any) {
            const errorMessage = err.message || 'Login failed. Please check your credentials.';
            
            // Check if this is a pending approval error
            if (errorMessage.includes('pending admin approval')) {
                setErrorType('warning');
                setError(errorMessage);
            } else if (errorMessage.includes('Invalid user credentials')) {
                setErrorType('error');
                setError('Invalid username or password. Please check your credentials and try again.');
            } else {
                setErrorType('error');
                setError(errorMessage);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    return (
        <PageContainer>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Card sx={{ maxWidth: 400, width: '100%' }}>
                    <CardContent sx={{ p: 4 }}>
                        {/* Header */}
                        <Typography
                            component="h1"
                            variant="h4"
                            sx={{
                                textAlign: 'center',
                                mb: 1,
                                fontWeight: 'bold',
                            }}
                        >
                            Login
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textAlign: 'center', mb: 3 }}
                        >
                            Sign in to access the EthicsVC platform
                        </Typography>

                        {/* Session expiry message */}
                        {sessionMessage && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                {sessionMessage}
                            </Alert>
                        )}

                        {/* Login Form */}
                        <Box component="form" onSubmit={handleSubmit}>
                            {/* Username Field */}
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="username"
                                label="Username"
                                name="username"
                                autoComplete="username"
                                autoFocus
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Person color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {/* Password Field */}
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lock color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPassword}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {/* Error Alert */}
                            {error && (
                                <Alert severity={errorType} sx={{ mt: 2 }}>
                                    {errorType === 'warning' && (
                                        <Box>
                                            <strong>Account Pending Approval</strong>
                                            <br />
                                            {error}
                                            <br />
                                            <em>Please contact an administrator if you believe this is an error.</em>
                                        </Box>
                                    )}
                                    {errorType === 'error' && error}
                                </Alert>
                            )}

                            {/* Submit Button */}
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={isLoading}
                                sx={{ mt: 3, mb: 2 }}
                            >
                                {isLoading ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={20} color="inherit" />
                                        <span>Signing in...</span>
                                    </Box>
                                ) : (
                                    'Sign In'
                                )}
                            </Button>

                            {/* Registration and Forgot Password Links */}
                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Typography variant="body2">
                                    Don't have an account?{' '}
                                    <a href="/register" style={{ color: '#1976d2', textDecoration: 'none' }}>
                                        Register here
                                    </a>
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Forgot your password?{' '}
                                    <a href="/forgot-password" style={{ color: '#1976d2', textDecoration: 'none' }}>
                                        Reset it here
                                    </a>
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </PageContainer>
    );
};

export default Login;
